import {
	ChangeDetectionStrategy,
	Component,
	inject,
	Inject,
	OnInit,
	SecurityContext,
} from '@angular/core';
import { Details, LeapOnboardingStatus } from '../../leap-api.service';
import { ICON } from '@core/util/icon';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { LeapOnboardingDialogData } from '../../entry-point/entry-point.types';
import { BaseComponent } from '@core/base.component';
import { DomSanitizer } from '@angular/platform-browser';
import { Address } from '@models/address';
import { LeapOnboardingData } from '../../leap-onboarding.models';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { Actions, ofActionDispatched, Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthUser } from '@state/auth/auth.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';
import { SignInWithCustomToken } from '@state/auth/auth.actions';
import {
	ILeapOnboardingService,
	LEAP_ONBOARDING_SERVICE,
} from '../../leap-onboarding.models';
import { LeapSdkService } from 'src/app/public/leap/leap-sdk.service';

export type VM = {
	lawFirmName: string;
	lawFirmAddress: string;
	lawFirmPhone: string;
	userName: string;
	userEmail: string;
	userPhone: string;
};

const DefaultVM = {
	lawFirmName: '',
	lawFirmAddress: '',
	lawFirmPhone: '',
	userName: '',
	userEmail: '',
	userPhone: '',
};

@UntilDestroy()
@Component({
	selector: 'app-leap-confirmation',
	templateUrl: './confirmation.component.html',
	styleUrls: ['./confirmation.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LeapConfirmationComponent
	extends BaseComponent<VM>
	implements OnInit
{
	@Select(AuthState.getLoggedInUser) loggedInUser$: Observable<AuthUser>;
	@Select(AuthState.loginChecked) loginChecked$: Observable<boolean>;

	public leapOnboardingStatus: LeapOnboardingStatus;
	public LeapOnboardingStatus = LeapOnboardingStatus;
	public ICON = ICON;
	public isLoading$: BehaviorSubject<boolean>;

	private onboardingService: ILeapOnboardingService = inject(
		LEAP_ONBOARDING_SERVICE,
	);

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: LeapOnboardingDialogData,
		private sanitizer: DomSanitizer,
		private authDispatch: AuthDispatch,
		private dialogRef: MatDialogRef<LeapConfirmationComponent>,
		private actions$: Actions,
		private leapSdkService: LeapSdkService,
	) {
		super(structuredClone(DefaultVM));
	}

	ngOnInit() {
		this.isLoading$ = this.onboardingService.isLoading$;

		this.leapOnboardingStatus =
			this.data.onboardingStatusResponse.leapOnboardingStatus;

		const newVM = this.getVmFromLeapLawFirm(
			this.data.onboardingStatusResponse.details,
		);
		this.VM = newVM;
	}

	handleCancelClick() {
		this.loggedInUser$.pipe(take(1)).subscribe((loggedInUser) => {
			if (loggedInUser) {
				this.authDispatch.LogOut();
			}
		});

		this.onboardingService.goToLoginScreen();
	}

	// Current design means that the user can only get to this page if they choose to create a new account from the login screen
	async handleConnectNowClick() {
		this.onboardingService.isLoading$.next(true);

		// Close this dialog when onboarding is successful (indicated by the SignInWithCustomToken action)
		this.actions$
			.pipe(
				untilDestroyed(this),
				ofActionDispatched(SignInWithCustomToken),
				take(1),
			)
			.subscribe(() => {
				this.dialogRef.close();
			});

		await this.onboardingService.subscribeToSetUser();

		this.onboardUserFromLeap();
	}

	private getLeapLawfirmAddress(address: Address) {
		const {
			street = '',
			street2 = '',
			unit = '',
			poBox = '',
			city = '',
			postcode = '',
			province = '',
		} = address;

		const unsafeAddressBlock = `${street}<br/>${street2 ? street2 + '<br/>' : ''}${unit ? unit + '<br/>' : ''}${poBox ? poBox + '<br/>' : ''}${city}${province ? ', ' + province : ''}<br/>${postcode}`;
		const safeAddressBlock = this.sanitizer.sanitize(
			SecurityContext.HTML,
			unsafeAddressBlock,
		);

		return safeAddressBlock;
	}

	private getVmFromLeapLawFirm(lawfirmDetails: Details): VM {
		const newVM: VM = {
			lawFirmName: lawfirmDetails.lawFirmName,
			lawFirmPhone: lawfirmDetails.lawFirmPhone ?? 'N/A',
			lawFirmAddress:
				this.getLeapLawfirmAddress(lawfirmDetails.address) ?? 'N/A',
			userName: lawfirmDetails.fullName,
			userPhone: lawfirmDetails.userPhone ?? 'N/A',
			userEmail: lawfirmDetails.userEmail,
		};

		return newVM;
	}

	private async onboardUserFromLeap(): Promise<void> {
		const { userId, firmId } = await this.leapSdkService.getUserInfo();

		const onboardingData: LeapOnboardingData = {
			leapUserId: userId,
			leapFirmId: firmId,
			accessToken: await this.leapSdkService.getAccessToken(),
			isForFirm:
				this.data.onboardingStatusResponse.leapOnboardingStatus ===
				LeapOnboardingStatus.NotOnboarded,
		};

		this.authDispatch.OnboardUserFromLeap(onboardingData);
	}
}
